// Boost Product Selection page for standalone offers (no campaign context)
// Uses offerId + memberId instead of programId + profileId

import { useProductSelectionState } from "@/lib/hooks/use-product-selection";
import type { MemberDataResponse } from "@/lib/queries/member-data";
import { useBoostContextByOfferQuery } from "@/lib/queries/products";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import type { OrgRouteContext } from "@/lib/types/route-context";
import { ProductSelectionPageView } from "@/routes/(org)/o.$orgSlug/p.$profileSlug/c.$campaignId/product-selection.$interactionId";
import type { OrderRequest } from "@/lib/hooks/use-product-selection-types";

const boostProductSelectionSearchSchema = z.object({
  pfaOrderId: z.string(),
  memberId: z.coerce.number().int().positive(),
  returnTo: z.string().optional(),
});

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/offers/$offerId/boost-product-selection",
)({
  component: BoostProductSelectionContainer,
  validateSearch: boostProductSelectionSearchSchema,
});

function BoostProductSelectionContainer() {
  const { offerId: offerIdParam, orgSlug } = Route.useParams();
  const { pfaOrderId, memberId, returnTo } = Route.useSearch();
  const context = Route.useRouteContext() as OrgRouteContext;
  const { organization } = context;
  const organizationId = organization.id;
  const offerId = Number(offerIdParam);
  const navigate = useNavigate();

  // Single server-side call resolves orderKey + orderRequest + memberData
  const {
    data: boostContext,
    isPending: isContextPending,
    error: boostContextError,
  } = useBoostContextByOfferQuery(
    organizationId,
    offerId,
    memberId,
    pfaOrderId,
  );

  // Propagate query errors to route error boundary for proper auth/service error handling
  if (boostContextError) throw boostContextError;

  // Note: Standalone offers don't have a profile context, so we skip profile query
  // Member data comes from boostContext instead
  const orderKey = boostContext?.orderKey ?? "";

  // Cart persistence key scoped to this specific boost order
  const cartStorageKey = `cart-boost-${organizationId}-${pfaOrderId}`;

  // Validate orderRequest shape from the API response
  const orderRequest = useMemo((): OrderRequest | null => {
    const raw = boostContext?.orderRequest;
    if (typeof raw !== "object" || raw === null) return null;
    if (!("id" in raw) || typeof raw.id !== "string") return null;
    if (!("type" in raw) || typeof raw.type !== "string") return null;
    return raw as OrderRequest;
  }, [boostContext?.orderRequest]);

  // Post-submit navigation callback
  const handleSubmitSuccess = useCallback(() => {
    const isSafeReturn =
      returnTo &&
      returnTo.startsWith("/") &&
      !returnTo.startsWith("//") &&
      !/^\/[a-z]+:/i.test(returnTo);
    if (isSafeReturn) {
      // Append memberId to returnTo so the destination page can validate it
      const separator = returnTo.includes("?") ? "&" : "?";
      void navigate({ to: `${returnTo}${separator}memberId=${memberId}` });
    } else {
      void navigate({
        to: "/o/$orgSlug/offers/$offerId",
        params: {
          orgSlug,
          offerId: String(offerId),
        },
        search: {
          memberId,
        },
      });
    }
  }, [navigate, returnTo, orgSlug, offerId, memberId]);

  // Narrow memberData: only pass if it has the expected shape
  const memberData = useMemo((): MemberDataResponse | undefined => {
    const md = boostContext?.memberData;
    if (!md || typeof md !== "object") return undefined;
    if (!("member" in md) || !("fields" in md)) return undefined;
    return md as MemberDataResponse;
  }, [boostContext?.memberData]);

  // Shared product selection state
  // Note: programId is not available for standalone offers, pass 0 as placeholder
  const hookResult = useProductSelectionState({
    organizationId,
    orderKey,
    programId: 0,
    cartStorageKey,
    orderRequest,
    isOrderPending: isContextPending,
    clientId: orderRequest?.clientId ?? "",
    isBoost: true,
    pfaOrderId,
    memberData,
    profileAddresses: undefined, // No profile context
    profileEmail: memberData?.member?.email,
    profileId: undefined, // No profile context
    onSubmitSuccess: handleSubmitSuccess,
    analyticsPrefix: "boost_product",
    analyticsExtraProps: { pfaOrderId, offerId, memberId },
    supportsNoCatalogue: false,
  });

  // Keep skeleton visible until products actually arrive to prevent
  // a flash of "No products found" during the initial fetch (EPD-6052).
  const isLoading =
    hookResult.isLoading ||
    (hookResult.isFetching && hookResult.products.length === 0);

  return (
    <ProductSelectionPageView
      key={`boost-${organizationId}-${pfaOrderId}`}
      interactionId={String(memberId)} // Use memberId as placeholder for interactionId
      {...hookResult}
      isLoading={isLoading}
    />
  );
}
